<template>
    <div class="onetime-auth-modal" v-if="showModal">
        <div class="modal-dialog modal-lg modal-dialog-scrollable" role="document">
            <div class="modal-content">
                <InnerLoader v-if="isLoading" />
                <div class="modal-header">
                    <div class="text-right">
                        <i
                            @click="closeModal"
                            class="fas text-grey fa-times text-xl shadow-text-none cursor-pointer"
                        ></i>
                    </div>
                    <div v-if="!isGoogle2fa">
                        <!-- OTP -->
                        <h2 class="text-xl text-center md:text-2xl my-1 shadow-text-none">
                            {{ translate('auth_header_login_otp') }}
                        </h2>
                        <p class="font-size-14 text-center shadow-text-none">
                            {{ translate('auth_header_login_otp_1') }}
                        </p>
                    </div>
                    <div v-else>
                        <!-- Google Authenticator -->
                        <h2 class="text-xl text-center md:text-2xl my-1 shadow-text-none">
                            {{ translate('auth_header_login_2fa') }}
                        </h2>
                        <p class="font-size-14 text-center shadow-text-none">
                            {{ translate('auth_header_login_2fa_1') }}
                        </p>
                    </div>
                </div>
                <div :class="{ 'modal-body flex justify-center': true, 'flex-col align-center': qrcodeImage }">
                    <div
                        v-if="qrcodeImage"
                        :class="{
                            'qr-content flex-1 p-4 pb-0 text-center shadow-text-none font-size-14': true,
                            'mt-2': qrcodeImage,
                            'mt-4': !qrcodeImage
                        }"
                    >
                        <p>{{ translate('2fa_modal_txt_setup') }}</p>
                        <div class="qr-image-box mx-auto my-2 md:my-4">
                            <img id="qr-image" :src="`data:image/png;base64, ${qrcodeImage}`" />
                        </div>
                        <p>{{ translate('2fa_modal_txt_setup1') }}</p>
                    </div>
                    <div :class="{ 'md:py-12': true, 'py-6 flex justify-center': qrcodeImage, 'py-8': !qrcodeImage }">
                        <otp-input
                            ref="otpInput"
                            input-classes="otp-input"
                            separator=""
                            input-type="number"
                            :num-inputs="6"
                            :should-auto-focus="true"
                            :is-input-num="true"
                            @on-change="handleOnChange"
                            @on-complete="handleOnComplete"
                        />

                        <p v-if="!isGoogle2fa" class="mt-4 text-center font-size-14 shadow-text-none">
                            ref: {{ loginOtpRef }}
                        </p>
                    </div>
                </div>
                <div class="modal-footer">
                    <div class="font-size-14 text-center shadow-text-none" v-if="!isGoogle2fa">
                        <span>{{
                            translate('auth_header_otp_not_received', {
                                countdown: resendCountdown >= 0 ? resendCountdown : ''
                            })
                        }}</span>
                        <span
                            v-if="enableResendOtp"
                            class="underline cursor-pointer"
                            @click="typeOneTime == 'login' ? resendLogin() : resendRegister()"
                            >{{ translate('auth_header_resend') }}</span
                        >
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import appConfig from '@/app.config.js'
import Request from '@/lib/request.js'
import OtpInput from '../../node_modules/@bachdgvn/vue-otp-input'
import InnerLoader from '@/components/InnerLoader'
import { load } from 'recaptcha-v3'
import popup from '@/lib/popup.js'
import helperMixins from '@/mixins/helper.js'

export default {
    components: {
        OtpInput,
        InnerLoader
    },

    mixins: [helperMixins],

    data() {
        return {
            showModal: false,
            otpNumber: null,
            enableResendOtp: false,
            resendCountdown: 0,
            fromLogIn: null,
            isGoogle2fa: false,
            loginToken: null,
            loginOtpRef: null,
            isOneTime: null,
            typeOneTime: null,
            isLoading: false,
            keyGoogle2fa: '',
            qrcodeImage: '',
            resendIndex: 0
        }
    },

    methods: {
        closeModal() {
            this.$emit('close')
        },

        handleOnChange(value) {
            this.otpNumber = value
        },

        handleOnComplete(value) {
            this.otpNumber = value
            this.resendIndex = 0
            const payload = {
                is_google2fa: this.isGoogle2fa,
                login_token: this.loginToken,
                otp: this.otpNumber,
                otp_ref: this.loginOtpRef,
                email: this.fromLogIn.email,
                auth_select: this.fromLogIn.auth_select,
                name: this.fromLogIn.name,
                type: this.typeOneTime,
                key_google2fa: this.keyGoogle2fa
            }

            if (typeof this.fromLogIn.schoolSlug != 'undefined') {
                payload.schoolSlug = this.fromLogIn.schoolSlug
                payload.role = 'teacher'
            }

            if (this.isSchoolPage()) {
                payload.email = this.fromLogIn.email
                payload.school_name = this.fromLogIn.school_name
                payload.school_district = this.fromLogIn.school_district
                payload.school_country = this.fromLogIn.school_country
                payload.school_country_name = this.fromLogIn.school_country_name
                payload.school_phone = this.fromLogIn.school_phone
                payload.school_slug = this.fromLogIn.school_slug
            }

            this.$emit('confirm', payload)
        },

        resendLogin() {
            this.enableResendOtp = false

            this.onOtp(this.fromLogIn)
            this.resendIndex++
        },

        async resendRegister() {
            this.enableResendOtp = false

            this.resendIndex++
            const request = new Request()

            const recaptcha = await load(this.getRecaptchaSiteKey(), {
                useRecaptchaNet: true,
                autoHideBadge: true
            })

            const token = await recaptcha.execute('validate_captcha', {
                action: 'validate_captcha'
            })

            this.fromLogIn.captcha_response = token
            await request
                .post(appConfig.api.resendRegister, this.fromLogIn)
                .then(res => {
                    this.$emit('on-otp-completed')
                    this.isGoogle2fa = res.data.data.is_google2fa

                    if (!this.isGoogle2fa) {
                        this.loginToken = res.data.data.token
                        this.loginOtpRef = res.data.data.otp_ref
                    }

                    this.showModal = true
                    this.resendCountdown = appConfig.countdownOtp
                    this.resendOtpCountdown()
                })
                .catch(err => {
                    const error = err.response.data
                    this.$emit('fail')

                    if (error.message == 'err_2fa') {
                        popup.alert(this.translate('auth_err_2fa'))
                    }

                    if (error.status_code == 401) {
                        if (error.data.status == 'suspended') {
                            return popup.alert(this.translate('auth_login_alert_lock_account'))
                        }
                        return popup.alert(this.translate('auth_login_alert_not_match'))
                    }
                })
        },

        async onOtp(data) {
            const request = new Request()
            this.fromLogIn = data

            const recaptcha = await load(this.getRecaptchaSiteKey(), {
                useRecaptchaNet: true,
                autoHideBadge: true
            })

            const token = await recaptcha.execute('validate_captcha', {
                action: 'validate_captcha'
            })

            this.fromLogIn.captcha_response = token

            await request
                .post(appConfig.api.otp, this.fromLogIn)
                .then(res => {
                    this.$emit('on-otp-completed')
                    this.isGoogle2fa = res.data.data.is_google2fa

                    if (!this.isGoogle2fa) {
                        this.loginToken = res.data.data.token
                        this.loginOtpRef = res.data.data.otp_ref
                    }

                    this.showModal = true
                    this.typeOneTime = 'login'
                    this.resendCountdown = appConfig.countdownOtp

                    if (!this.isGoogle2fa) {
                        this.resendOtpCountdown()
                    }
                })
                .catch(err => {
                    const error = err.response.data
                    this.$emit('fail')

                    if (error.message == 'err_2fa') {
                        popup.alert(this.translate('auth_err_2fa'))
                    }

                    if (error.status_code == 401) {
                        if (error.data.status == 'suspended') {
                            return popup.alert(this.translate('auth_login_alert_lock_account'))
                        }
                        return popup.alert(this.translate('auth_login_alert_not_match'))
                    }
                })
        },

        resendOtpCountdown() {
            if (this.resendCountdown >= 0) {
                this.enableResendOtp = false
                setTimeout(() => {
                    this.resendCountdown--
                    this.resendOtpCountdown()
                }, 1000)
            } else {
                this.resendCountdown = ''
                this.enableResendOtp = true
            }
        },

        handleExpireOtp() {
            let form = {
                login_token: this.loginToken,
                otp_ref: this.loginOtpRef
            }

            const request = new Request()
            const url = appConfig.api.setExpireOtp

            request.post(url, form).then(() => {
                if (this.resendIndex < 5) {
                    this.enableResendOtp = true
                }
            })
        },

        clearInput() {
            this.$refs.otpInput.clearInput()
        },

        onShow(data) {
            this.fromLogIn = data
            const request = new Request()

            request
                .post(appConfig.api.checkOneTime, this.fromLogIn)
                .then(res => {
                    this.$emit('on-otp-completed')
                    this.isGoogle2fa = res.data.data.is_google2fa

                    if (!this.isGoogle2fa) {
                        this.loginToken = res.data.data.token
                        this.loginOtpRef = res.data.data.otp_ref
                    }

                    this.keyGoogle2fa = this.fromLogIn.key_google2fa
                    this.qrcodeImage = this.fromLogIn.qrcode_image
                    this.showModal = true
                    this.typeOneTime = 'register'
                    this.resendCountdown = appConfig.countdownOtp

                    if (!this.isGoogle2fa) {
                        this.resendOtpCountdown()
                    }

                    if (this.$store) {
                        this.$store.commit('toggleMainLoader', false)
                    }
                })
                .catch(err => {
                    if (this.$store) {
                        this.$store.commit('toggleMainLoader', false)
                    }

                    if (err.response.data.message == 'err_2fa') {
                        popup.alert(this.translate('auth_err_2fa'))
                    }
                })
        },

        showLoader(status) {
            this.isLoading = status
        },

        getRecaptchaSiteKey() {
            return process.env.VUE_APP_RECAPTCHA_SITE_KEY
        }
    }
}
</script>

<style lang="scss">
.onetime-auth-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding-top: 30px;
    align-items: flex-start;
    background-color: rgba(0, 0, 0, 0.2);
    z-index: 100;

    .modal-dialog {
        z-index: 2;

        @media screen and (max-width: 767px) {
            padding: 0 0.5rem;
        }

        .modal-content {
            position: relative;
            background-color: #fff;
            width: 500px;
            margin: 0 auto;
            padding: 1rem;
            overflow: hidden;
            border-radius: 3px;
            box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.6);

            @media screen and (max-width: 767px) {
                width: 100%;

                .py-8 {
                    padding-top: 2rem;
                    padding-bottom: 2rem;
                }
            }

            .inner-loader {
                top: 0;
                left: 0;
            }

            .font-size-14 {
                font-size: 14px;
            }

            .modal-body {
                .qr-content {
                    @media screen and (max-width: 767px) {
                        padding: 0;
                    }

                    .qr-image-box {
                        max-width: 200px;

                        @media screen and (max-width: 767px) {
                            max-width: 120px;
                        }

                        img {
                            width: 100%;
                        }
                    }
                }

                .marketplace-content {
                    .marketplace-image-box {
                        max-width: 250px;
                        @media screen and (max-width: 767px) {
                            max-width: 120px;
                        }

                        img {
                            width: 100%;
                        }
                    }
                }

                .otp-input {
                    width: 40px;
                    height: 45px;
                    padding: 5px;
                    margin: 0 2px;
                    font-size: 20px;
                    border-radius: 4px;
                    border: 1px solid rgba(0, 0, 0, 0.3);
                    text-align: center;
                    -moz-appearance: textfield;

                    &::-webkit-outer-spin-button,
                    &::-webkit-inner-spin-button {
                        -webkit-appearance: none;
                        margin: 0;
                    }

                    &.error {
                        border: 1px solid red !important;
                    }

                    @media screen and (max-width: 400px) {
                        width: 35px;
                        height: 40px;
                        font-size: 18px;
                    }
                }
                .otp-input::-webkit-inner-spin-button,
                .otp-input::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }

                .md\:py-12 {
                    padding-top: 3rem;
                    padding-bottom: 3rem;
                }
            }
        }
    }
}
</style>
